/**
 * sessionStorage keys and id helpers shared by the `/nrs/download` funnel
 * instrumentation.
 *
 * The download page, the shared signup modal, and the OAuth callback handler
 * all read and write these same values so funnel state survives the OAuth
 * redirect round trip. They are centralized because a mismatched key breaks
 * attribution silently: events still post, they just stop correlating.
 */

/** Download slug the visitor asked for before being sent through auth. */
export const PENDING_DOWNLOAD_SLUG_KEY = "nrs_pending_download_slug";

/** Per-tile-click correlation id for the click -> auth -> session join. */
export const PENDING_DOWNLOAD_FLOW_ID_KEY = "nrs_pending_download_flow_id";

/** Provider the visitor picked, so the OAuth return can attribute success. */
export const PENDING_AUTH_PROVIDER_KEY = "nrs_pending_auth_provider";

/** Prefix marking a flow's auth success as recorded, to avoid double counting. */
export const AUTH_SUCCESS_RECORDED_FLOW_KEY_PREFIX = "nrs_auth_success_recorded_flow_";

/**
 * Per-visit correlation id minted when `/nrs/download` loads.
 *
 * Distinct from the flow id: a visitor gets one visit id for the whole visit
 * but a new flow id per tile click. The visit id is the funnel's unit of
 * measure, letting every stage from page view to upgrade count visitors rather
 * than comparing click totals against server rows.
 */
export const DOWNLOAD_VISIT_ID_KEY = "nrs_download_visit_id";

/** Prefix marking a visit's page-view event as already recorded. */
export const DOWNLOAD_PAGE_VIEW_RECORDED_KEY_PREFIX = "nrs_download_page_view_recorded_";

/**
 * Prefix marking that this visit's `download_page_view` row has already been
 * patched with `client_form_factor = mobile`. Platform detection resolves
 * asynchronously, after the page-view event already fired, so this guards a
 * second, patching event from firing more than once per visit. See
 * docs/MOBILE_DOWNLOAD_EMAIL_CAPTURE_ARCHITECTURE_AND_DESIGN.md section 7.6.
 */
export const DOWNLOAD_PAGE_VIEW_FORM_FACTOR_PATCHED_KEY_PREFIX =
  "nrs_download_page_view_form_factor_patched_";

/**
 * Prefix marking that `mobile_download_email_link_opened` has already been
 * recorded for a given `dl_ref` in this browser session, so a visitor who
 * clicks more than one tile on the same visit is not double-counted as two
 * "opens" of the emailed link.
 */
export const DOWNLOAD_EMAIL_LINK_OPENED_RECORDED_KEY_PREFIX =
  "nrs_download_email_link_opened_recorded_";

/** 16-lowercase-hex, matching `secrets.token_hex(8)` on the Composer side. */
const DL_REF_PATTERN = /^[0-9a-f]{16}$/;

/**
 * Reads and validates `?dl_ref=` from the current URL. Malformed or missing
 * values return undefined rather than throwing, so a tampered or truncated
 * link degrades to a normal (unattributed) download page visit instead of
 * breaking the page.
 */
export function readDlRefFromLocation(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  const raw = new URLSearchParams(window.location.search).get("dl_ref");
  const trimmed = raw?.trim().toLowerCase();
  return trimmed && DL_REF_PATTERN.test(trimmed) ? trimmed : undefined;
}

/**
 * Generates a correlation id. Returns undefined where `crypto.randomUUID` is
 * unavailable (non-secure contexts); callers then emit events without an id
 * rather than inventing a weaker one that would corrupt distinct-visitor counts.
 */
export function createFunnelCorrelationId(): string | undefined {
  if (typeof crypto === "undefined" || typeof crypto.randomUUID !== "function") {
    return undefined;
  }
  return crypto.randomUUID();
}

/** Reads the current visit id without minting one. */
export function readDownloadVisitId(): string | undefined {
  if (typeof sessionStorage === "undefined") {
    return undefined;
  }
  try {
    const stored = sessionStorage.getItem(DOWNLOAD_VISIT_ID_KEY);
    return typeof stored === "string" && stored.trim() ? stored.trim() : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Returns this visit's id, minting and persisting one on first call. Reused on
 * remount so returning from the OAuth redirect continues the same visit instead
 * of registering a second visitor.
 */
export function resolveDownloadVisitId(): string | undefined {
  const existing = readDownloadVisitId();
  if (existing) {
    return existing;
  }
  const minted = createFunnelCorrelationId();
  if (!minted || typeof sessionStorage === "undefined") {
    return undefined;
  }
  try {
    sessionStorage.setItem(DOWNLOAD_VISIT_ID_KEY, minted);
  } catch {
    return undefined;
  }
  return minted;
}
